import { useState, useCallback, useEffect, useRef } from 'react';
import usePerRoomState, { stateManager } from './usePerRoomState';
import logger from '../utils/logger';

/**
 * Hook for managing message edit functionality
 * Handles edit state, content restoration, and Matrix protocol formatting
 * Supports per-room state management
 */
export const useEdit = (roomId) => {
  const [editingMessage, setEditingMessage] = useState(null);
  const { getState, setState, clearState } = usePerRoomState('edit', roomId);
  const previousRoomId = useRef(roomId);

  // Handle room switching
  useEffect(() => {
    logger.debug(`[useEdit] Room change detected. Previous: ${previousRoomId.current}, Current: ${roomId}, EditingMessage:`, editingMessage);
    
    if (previousRoomId.current !== roomId) {
      // Save state for previous room if we had one
      if (previousRoomId.current && editingMessage) {
        logger.debug(`[useEdit] Saving edit state for room ${previousRoomId.current}:`, editingMessage);
        // Save to previous room using the singleton state manager
        stateManager.setState('edit', previousRoomId.current, editingMessage);
      }
      
      // Restore state for new room
      if (roomId) {
        const savedState = getState();
        logger.debug(`[useEdit] Restoring edit state for room ${roomId}:`, savedState);
        setEditingMessage(savedState);
      } else {
        logger.debug('[useEdit] No roomId, clearing state');
        setEditingMessage(null);
      }
      
      previousRoomId.current = roomId;
    }
  }, [roomId, editingMessage, getState]);

  // Save state when editingMessage changes (but not during room switches)
  useEffect(() => {
    if (roomId && previousRoomId.current === roomId) {
      logger.debug(`[useEdit] Saving current edit state for room ${roomId}:`, editingMessage);
      setState(editingMessage);
    }
  }, [roomId, editingMessage, setState]);

  /**
   * Start editing a message
   * @param {Object} message - The message object to edit
   * @param {string} content - Original content of the message being edited
   */
  const startEdit = useCallback((message, content) => {
    setEditingMessage({
      id: message.getId(),
      originalContent: content,
      originalEvent: message, // Store the full event for Matrix protocol
    });
  }, []);

  /**
   * Cancel the current edit
   */
  const cancelEdit = useCallback(() => {
    setEditingMessage(null);
    clearState();
  }, [clearState]);

  /**
   * Restore edit state from sessionStorage (without Matrix event)
   * Used when restoring draft after page refresh
   * @param {Object} editData - Plain object from storage {id, originalContent}
   */
  const restoreEditFromStorage = useCallback((editData) => {
    if (!editData || !editData.id) return;
    
    // Validate Matrix event ID format (should start with $)
    if (typeof editData.id !== 'string' || !editData.id.startsWith('$')) {
      logger.warn('[useEdit] Invalid event ID in stored edit data', editData);
      return;
    }
    
    logger.debug('[useEdit] Restoring edit from storage', editData);
    
    setEditingMessage({
      id: editData.id,
      originalContent: editData.originalContent || '',
      originalEvent: null, // No event available from storage
      restoredFromStorage: true, // Flag to indicate this was restored
    });
  }, []);

  /**
   * Get Matrix protocol content for edit
   * @param {string} newMessageBody - The edited message content
   * @returns {Object} Matrix message content with edit metadata
   */
  const getEditContent = useCallback((newMessageBody) => {
    if (!editingMessage) return null;

    // If we have the original event, use it
    if (editingMessage.originalEvent) {
      const originalEvent = editingMessage.originalEvent;
      const originalContent = originalEvent.getContent();
      
      // Base edit content structure
      const editContent = {
        body: newMessageBody,
        msgtype: 'm.text',
        'm.new_content': {
          body: newMessageBody,
          msgtype: 'm.text',
        },
        'm.relates_to': {
          rel_type: 'm.replace',
          event_id: originalEvent.getId(),
        },
      };

      // If the original message already has m.relates_to, forward it to m.new_content
      if (originalContent['m.relates_to']) {
        editContent['m.new_content']['m.relates_to'] = originalContent['m.relates_to'];
      }

      return editContent;
    }
    
    // Fallback: If restored from storage, use the stored ID
    if (editingMessage.restoredFromStorage && editingMessage.id) {
      logger.warn('[useEdit] Using stored edit ID (no original event)');
      return {
        body: newMessageBody,
        msgtype: 'm.text',
        'm.new_content': {
          body: newMessageBody,
          msgtype: 'm.text',
        },
        'm.relates_to': {
          rel_type: 'm.replace',
          event_id: editingMessage.id, // Use stored ID directly
        },
      };
    }
    
    logger.error('[useEdit] Cannot create edit content - no event or ID');
    return null;
  }, [editingMessage]);

  return {
    editingMessage,
    startEdit,
    cancelEdit,
    getEditContent,
    restoreEditFromStorage,
  };
};

export default useEdit;